// Can't use aliases here because we're doing exports/require
import { DOMAIN } from 'config';
import * as URLParams from 'constants/urlParams';
import ShortUrl from 'services/shortUrl';
import * as PAGES from '../constants/pages';
import { parseURI, buildURI } from '../util/lbryURI';
import * as COLLECTIONS_CONSTS from '../constants/collections';

function encodeWithSpecialCharEncode(string: string): string {
  // encodeURIComponent doesn't encode `'` and others
  // which other services may not like
  return encodeURIComponent(string).replace(/'/g, '%27').replace(/\(/g, '%28').replace(/\)/g, '%29');
}

export const formatLbryUrlForWeb = (uri: string): string => {
  if (!uri) return uri;
  let newUrl = uri.replace('lbry://', '/').replace(/#/g, ':');

  if (newUrl.startsWith('/?')) {
    // This is a lbry link to an internal page ex: lbry://?rewards
    newUrl = newUrl.replace('/?', '/$/');
  }

  return newUrl;
};
export const formatLbryChannelName = (uri: string): string => uri && uri.replace('lbry://', '').replace(/#/g, ':');
export const formatFileSystemPath = (path: string): string | undefined => {
  if (!path) {
    return;
  }

  let webUrl = path.replace(/\\/g, '/');

  if (webUrl[0] !== '/') {
    webUrl = `/${webUrl}`;
  }

  return encodeURI(`file://${webUrl}`).replace(/[?#]/g, encodeURIComponent);
};

/*
  Function that handles page redirects
  ex: lbry://?rewards
  ex: open.lbry.com/?rewards
*/
export const formatInAppUrl = (path: string): string => {
  // Determine if we need to add a leading "/$/" for app pages
  const APP_PAGE_REGEX = /(\?)([a-z]*)(.*)/;
  const appPageMatches = APP_PAGE_REGEX.exec(path);

  if (appPageMatches && appPageMatches.length) {
    // Definitely an app page (or it's formatted like one)
    const [, , page, queryString] = appPageMatches;

    if (Object.values(PAGES).includes(page as any)) {
      let actualUrl = '/$/' + page;

      if (queryString) {
        actualUrl += `?${queryString.slice(1)}`;
      }

      return actualUrl;
    }
  }

  // Regular claim url
  return path;
};
export const formatWebUrlIntoLbryUrl = (pathname: string, search: string): string => {
  // If there is no uri, the user is on an internal page
  // pathname will either be "/" or "/$/{page}"
  const path = pathname.startsWith('/$/') ? pathname.slice(3) : pathname.slice(1);
  let appLink = `lbry://?${path || PAGES.DISCOVER}`;

  if (search) {
    // We already have a leading "?" for the query param on internal pages
    appLink += search.replace('?', '&');
  }

  return appLink;
};
export const generateInitialUrl = (hash: string): string => {
  let url = '/';

  if (hash) {
    hash = hash.replace('#', '');
    url = hash.startsWith('/') ? hash : '/' + hash;
  }

  return url;
};
export const generateLbryContentUrl = (canonicalUrl: string, permanentUrl: string): string => {
  return canonicalUrl ? canonicalUrl.split('lbry://')[1] : permanentUrl.split('lbry://')[1];
};
export const generateLbryWebUrl = (lbryUrl: string): string => {
  return lbryUrl.replace(/#/g, ':');
};
export const generateEncodedLbryURL = (
  domain: string,
  lbryWebUrl: string,
  includeStartTime: boolean,
  startTime: number,
  listId?: string
): string => {
  let urlParams = new URLSearchParams();

  if (includeStartTime) {
    urlParams.append('t', startTime.toString());
  }

  if (listId) {
    urlParams.append(COLLECTIONS_CONSTS.COLLECTION_ID, listId);
  }

  const urlParamsString = urlParams.toString();
  const encodedPart = encodeWithSpecialCharEncode(`${lbryWebUrl}?${urlParamsString}`);
  return `${domain}/${encodedPart}`;
};
export const generateShareUrl = (
  domain: string,
  lbryUrl: string,
  referralCode: string,
  rewardsApproved: boolean,
  includeStartTime: boolean,
  startTime: number,
  listId: string | undefined,
  viewKeySigData: ChannelSignResponse
): string => {
  let urlParams = new URLSearchParams();

  if (referralCode && rewardsApproved) {
    urlParams.append('r', referralCode);
  }

  if (listId) {
    urlParams.append(COLLECTIONS_CONSTS.COLLECTION_ID, listId);
  }

  if (includeStartTime) {
    urlParams.append('t', startTime.toString());
  }

  if (viewKeySigData) {
    urlParams.append('signature', viewKeySigData.signature);
    urlParams.append('signature_ts', viewKeySigData.signing_ts);
  }

  const urlParamsString = urlParams.toString();
  const { streamName, streamClaimId, channelName, channelClaimId } = parseURI(lbryUrl);
  let uriParts = {
    ...(streamName
      ? {
          streamName: encodeWithSpecialCharEncode(streamName),
        }
      : {}),
    ...(streamClaimId
      ? {
          streamClaimId,
        }
      : {}),
    ...(channelName
      ? {
          channelName: encodeWithSpecialCharEncode(channelName),
        }
      : {}),
    ...(channelClaimId
      ? {
          channelClaimId,
        }
      : {}),
  };
  const encodedUrl = buildURI(uriParts, false, false);
  const lbryWebUrl = encodedUrl.replace(/#/g, ':');
  const url = `${domain}/${lbryWebUrl}` + (urlParamsString === '' ? '' : `?${urlParamsString}`);
  return url;
};
export const generateShortShareUrl = async (
  domain: string,
  lbryUrl: string,
  referralCode: string,
  rewardsApproved: boolean,
  includeStartTime: boolean,
  startTime: number,
  listId: string | undefined,
  uriAccessKey?: UriAccessKey
): Promise<string> => {
  type Params = Array<[string, string | null | undefined]>;
  const paramsToShorten: Params = [
    ['signature', uriAccessKey ? uriAccessKey.signature : null],
    ['signature_ts', uriAccessKey ? uriAccessKey.signature_ts : null],
    [COLLECTIONS_CONSTS.COLLECTION_ID, listId || null],
    ['r', referralCode && rewardsApproved ? referralCode : null],
  ];
  const paramsToRetain: Params = [['t', includeStartTime ? startTime.toString() : null]];
  // -- Build base URL with claim gists:
  const { streamName, streamClaimId, channelName, channelClaimId } = parseURI(lbryUrl);
  const uriParts = {
    ...(streamName
      ? {
          streamName: encodeWithSpecialCharEncode(streamName),
        }
      : {}),
    ...(streamClaimId
      ? {
          streamClaimId,
        }
      : {}),
    ...(channelName
      ? {
          channelName: encodeWithSpecialCharEncode(channelName),
        }
      : {}),
    ...(channelClaimId
      ? {
          channelClaimId,
        }
      : {}),
  };
  const encodedUrl = buildURI(uriParts, false, false);
  const lbryWebUrl = encodedUrl.replace(/#/g, ':');
  const baseUrl = `${domain}/${lbryWebUrl}`;
  // -- Append params that we want to shorten:
  const urlToShorten = new URL(baseUrl);
  paramsToShorten.forEach((p) => {
    if (p[1]) {
      urlToShorten.searchParams.set(p[0], p[1]);
    }
  });
  // -- Fetch the short url:
  const shortUrl = await ShortUrl.createFrom(urlToShorten.toString())
    .then((res: ShortUrlResponse) => {
      return res.short_url;
    })
    .catch((err) => {
      assert(false, 'ShortUrl api failed, returning original', err);
      return urlToShorten.toString();
    });
  // -- Put remaining params that we want in original form:
  const finalUrl = new URL(shortUrl);
  paramsToRetain.forEach((p) => {
    if (p[1]) {
      finalUrl.searchParams.set(p[0], p[1]);
    }
  });
  // -- Profit
  return finalUrl.toString();
};
export const generateRssUrl = (
  domain: string,
  channelClaim: { value_type?: string; canonical_url?: string } | null
): string => {
  if (!channelClaim || channelClaim.value_type !== 'channel' || !channelClaim.canonical_url) {
    return '';
  }

  const url = `${domain}/$/rss/${channelClaim.canonical_url.replace('lbry://', '').replace('#', ':')}`;
  return url;
};
export const generateListSearchUrlParams = (collectionId: string): string => {
  const urlParams = new URLSearchParams();
  urlParams.set(COLLECTIONS_CONSTS.COLLECTION_ID, collectionId);
  return `?` + urlParams.toString();
};
// Google cache url
// ex: webcache.googleusercontent.com/search?q=cache:MLwN3a8fCbYJ:https://lbry.tv/%40Bombards_Body_Language:f+&cd=12&hl=en&ct=clnk&gl=us
// Extract the lbry url and use that instead
// Without this it will try to render lbry://search
export function generateGoogleCacheUrl(search: string, path: string): void {
  const googleCacheRegex = new RegExp(`(https://${DOMAIN}/)([^+]*)`);
  const [x, y, googleCachedUrl] = search.match(googleCacheRegex); // eslint-disable-line

  if (googleCachedUrl) {
    const actualUrl = decodeURIComponent(googleCachedUrl);

    if (actualUrl) {
      path = actualUrl.replace(/:/g, '#');
    }
  }
}
export const getPathForPage = (page: string): string => `/$/${page}/`;
export const getModalUrlParam = (modal: string, modalParams: Record<string, unknown> = {}): string => {
  const urlParams = new URLSearchParams();
  urlParams.set(URLParams.MODAL, modal);
  urlParams.set(URLParams.MODAL_PARAMS, encodeURIComponent(JSON.stringify(modalParams)));
  const embedUrlParams = urlParams.toString();
  return embedUrlParams;
};
// This is NOT a full HTML decoder.
// It intentionally decodes only a small set of common entities
// to salvage malformed URLs without breaking query parameters
// like "&currency".
export function htmlDecode(str: string): string {
  if (typeof str !== 'string' || !str.includes('&')) return str;
  return str
    .replace(/&quot;/gi, '"')
    .replace(/&#0*34;/gi, '"')
    .replace(/&#x27;/gi, "'")
    .replace(/&#0*39;/gi, "'")
    .replace(/&apos;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&amp;/gi, '&');
}
